import { useEffect, useMemo, useState } from 'react';
import { hasAnyContent } from 'utils/messageSection';
import { PhoneLayout } from 'components';
import {
  TMessageSection,
  EContentSectionType,
  TMediaSection,
  TAssetMessageSection,
} from 'types';
import { sendAllMessageStrings } from '../../../constants';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { EOrganizationPlan } from 'types/enum';
import { Icon } from '../../../components/Icon';
import { findAssetById, TPreviewMessageAsset } from 'utils/messagePreview';
import { previewNameInsertion } from 'utils/nameInsertion';
import {
  AssetBubble,
  ImageBubble,
  isCardCarouselAsset,
  TextBubble,
  VideoBubble,
} from 'components/MessagePreview';

type Props = {
  sections: TMessageSection[];
  assets?: TPreviewMessageAsset[];
  buttonLabel?: string;
  bubbleMaxWidth?: string;
  /**
   * 本文の後ろに独立して届くCTA。自動応答のチャットリンクで使う。
   * 本文最後のTEXTへボタンを足す `buttonLabel` とは別のメッセージとして描画する。
   */
  chatLinkCta?: { text: string; label: string };
  /** 履歴は送信当時のボタンを、現在の契約プランによらず表示する。 */
  historical?: boolean;
  /** 一斉送信の作成・下書き・予約だけ、本文の名前差し込みを表示する。 */
  enableNameInsertionPreview?: boolean;
};

const isAssetSection = (
  section: TMessageSection,
): section is TAssetMessageSection =>
  section.type === EContentSectionType.RICH_MESSAGE ||
  section.type === EContentSectionType.CARD_TYPE_MESSAGE;

const isMediaSection = (section: TMessageSection): section is TMediaSection =>
  section.type === EContentSectionType.IMAGE ||
  section.type === EContentSectionType.VIDEO;

function MessagePreview({
  sections,
  assets,
  buttonLabel,
  bubbleMaxWidth = 'max-w-[78%]',
  chatLinkCta,
  historical = false,
  enableNameInsertionPreview = false,
}: Props) {
  const [fileUrlCache, setFileUrlCache] = useState<{ [key: string]: string }>(
    {},
  );
  const { data: currentUser } = useCurrentUser();

  const getFileUrl = (section: TMediaSection): string | null => {
    if (!section.file) return null;

    const cacheKey = `${section.id}-${section.file.name}`;

    if (!fileUrlCache[cacheKey]) {
      const url = URL.createObjectURL(section.file);
      setFileUrlCache((prev) => ({ ...prev, [cacheKey]: url }));
      return url;
    }

    return fileUrlCache[cacheKey];
  };

  useEffect(() => {
    return () => {
      Object.values(fileUrlCache).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [fileUrlCache]);

  const lastTextIndex = useMemo(() => {
    if (!buttonLabel) return -1
    for (let i = sections.length - 1; i >= 0; i--) {
      const s = sections[i]
      if (s.type === EContentSectionType.TEXT && s.content?.trim()) return i
    }
    return -1
  }, [sections, buttonLabel])

  const renderMessage = (section: TMessageSection, index: number) => {
    // TEXT
    if (section.type === EContentSectionType.TEXT) {
      const hasText = section.content.trim().length > 0;
      if (!hasText) return null;

      const isLightPlan = currentUser?.organizationPlan === EOrganizationPlan.LIGHT
      const showButton = (historical || !isLightPlan) && index === lastTextIndex

      return (
        <div key={section.id} className="mb-4 flex break-all items-start">
          <div className={bubbleMaxWidth}>
            <TextBubble
              text={enableNameInsertionPreview ? previewNameInsertion(section.content) : section.content}
              footerLabel={showButton ? buttonLabel : undefined}
            />
          </div>
        </div>
      );
    }

    // FILE (PDF)
    if (section.type === EContentSectionType.FILE) {
      const fileName = section.file?.name || section.originalMedia?.fileName;
      if (!fileName) return null;

      return (
        <div key={section.id} className="mb-4 flex break-all items-start">
          <div className={bubbleMaxWidth}>
            <div className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-2.5">
              <Icon icon="attach" width={20} height={20} color="#9CA3AF" />
              <div className="min-w-0">
                <div className="truncate text-sm text-neutral-600">
                  {fileName}
                </div>
                <div className="text-xs text-neutral-400">
                  {sendAllMessageStrings.pdfLabel}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // IMAGE / VIDEO
    if (isMediaSection(section)) {
      const fileUrl = section.file ? getFileUrl(section) : section.originalMedia?.url;
      if (!fileUrl) return null;

      return (
        <div key={section.id} className="mb-4 flex break-all items-start">
          <div className="max-w-[70%]">
            {section.type === EContentSectionType.IMAGE ? (
              <ImageBubble url={fileUrl} />
            ) : (
              <VideoBubble
                url={fileUrl}
                actionLabel={
                  section.showUrlInput
                    ? section.actionLabel?.trim() ||
                      sendAllMessageStrings.actionLabelDefault
                    : undefined
                }
              />
            )}
          </div>
        </div>
      );
    }

    if (isAssetSection(section)) {
      if (!section.assetId) return null;

      const asset = findAssetById(assets, section.assetId);
      const isCardCarousel = isCardCarouselAsset(asset);

      return (
        <div
          key={section.id}
          data-testid={isCardCarousel ? 'card-carousel-section' : undefined}
          className={`mb-4 flex break-all items-start ${isCardCarousel ? 'w-full' : ''}`}
        >
          <div
            data-testid={isCardCarousel ? 'card-carousel-wrapper' : undefined}
            className={isCardCarousel ? 'w-full min-w-0' : 'max-w-[85%]'}
          >
            <AssetBubble
              kind={
                section.type === EContentSectionType.RICH_MESSAGE
                  ? 'richMessage'
                  : 'cardTypeMessage'
              }
              asset={asset}
              fallbackLabel={section.assetName}
            />
          </div>
        </div>
      );
    }

    if (section.type === EContentSectionType.SURVEY) {
      if (!section.surveyId) return null;

      const imageUrl = section.imageUrl?.trim();
      const aspectClass =
        section.imageAspectRatio === '1:1' ? 'aspect-square' : 'aspect-video';

      return (
        <div key={section.id} className="mb-4 flex break-all items-start">
          <div className={bubbleMaxWidth}>
            <div className="relative flex-1 overflow-hidden rounded-xl border border-neutral-200 bg-transparent">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt=""
                  className={`w-full rounded-t-xl object-cover ${aspectClass}`}
                />
              ) : null}
              <div className="whitespace-pre-wrap px-3 py-2 text-neutral-600 text-normal">
                {section.content?.trim() || 'アンケート'}
              </div>
              <div className="border-t border-neutral-200 bg-white px-3 py-2 text-center">
                <span className="text-sm font-normal text-blue-500">
                  回答する
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const hasContentFlag = hasAnyContent(sections);

  return (
    <PhoneLayout title={sendAllMessageStrings.preview}>
      {hasContentFlag ? (
        <div className="flex flex-col">
          {sections.map((section, index) => renderMessage(section, index))}
          {chatLinkCta ? (
            <div className="mb-4 flex break-all items-start">
              <div className={bubbleMaxWidth}>
                <TextBubble
                  text={chatLinkCta.text}
                  footerLabel={chatLinkCta.label}
                />
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="py-8 text-center text-sm text-neutral-400">
          {sendAllMessageStrings.showPreviewMessage}
        </div>
      )}
    </PhoneLayout>
  );
}

export default MessagePreview;
